import {FormControl, Radio, RadioGroup} from '@primer/react'
import type {PlaygroundResponseFormat} from '../../../types'
import {useCallback, useState} from 'react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {JsonSchemaDialog} from './JsonSchemaDialog'
import {testIdProps} from '@github-ui/test-id-props'

import styles from './ModelResponseFormat.module.css'

export function ModelResponseFormat({
  responseFormat,
  handleResponseFormatChange,
  jsonSchema,
  handleJsonSchemaChange,
  onSinglePlaygroundView,
}: {
  responseFormat: PlaygroundResponseFormat
  handleResponseFormatChange: (selectedResponseFormat: PlaygroundResponseFormat) => void
  jsonSchema?: string
  handleJsonSchemaChange: (jsonSchema: string) => void
  onSinglePlaygroundView?: boolean
}) {
  const jsonSchemaFeatureEnabled = useFeatureFlag('github_models_json_schema_structured_outputs')
  const showJsonSchemaUI = jsonSchemaFeatureEnabled && onSinglePlaygroundView

  const [showDialog, setShowDialog] = useState(false)

  const handleResponseFormatSelection = useCallback(
    (selectedResponseFormat: PlaygroundResponseFormat) => {
      if (selectedResponseFormat === 'json_schema') {
        setShowDialog(true)
      } else {
        setShowDialog(false)
      }

      handleResponseFormatChange(selectedResponseFormat)
    },
    [handleResponseFormatChange],
  )

  const handleSubmitJsonSchemaDialog = useCallback(
    (input: string) => {
      handleJsonSchemaChange(input)
      setShowDialog(false)
    },
    [handleJsonSchemaChange, setShowDialog],
  )

  return (
    <>
      <div {...testIdProps('response-format')}>
        <RadioGroup
          name="Response formats"
          onChange={value => handleResponseFormatSelection(value as PlaygroundResponseFormat)}
        >
          <RadioGroup.Label>Response format</RadioGroup.Label>
          <div className="d-flex gap-3">
            <FormControl>
              <Radio name="text-format" value="text" checked={responseFormat === 'text'} />
              <FormControl.Label>Text</FormControl.Label>
            </FormControl>
            <FormControl>
              <Radio name="json-object-format" value="json_object" checked={responseFormat === 'json_object'} />
              <FormControl.Label>JSON</FormControl.Label>
            </FormControl>
            {showJsonSchemaUI && (
              <FormControl>
                <Radio name="json-schema-format" value="json_schema" checked={responseFormat === 'json_schema'} />
                <FormControl.Label>Schema</FormControl.Label>
              </FormControl>
            )}
          </div>
          <div className={styles.caption}>Set the format for the model response.</div>
        </RadioGroup>
      </div>

      {showJsonSchemaUI && showDialog && (
        <JsonSchemaDialog
          onClose={() => setShowDialog(false)}
          onSubmit={handleSubmitJsonSchemaDialog}
          jsonSchema={jsonSchema}
        />
      )}
    </>
  )
}

try{ ModelResponseFormat.displayName ||= 'ModelResponseFormat' } catch {}